import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";

// Icons
import { PencilIcon, TrashIcon, } from "@heroicons/react/24/outline";

// Context
import SnackbarContext from "../../../context/psikotest/SnackbarContext";
import LoaderContext from "../../../context/psikotest/LoaderContext";

// Helper
import errorHandler from "../../../helper/psikotest/errorHandler";
import logRender from "../../../helper/psikotest/logRender";

// Controller
import Cfit from "../../../controller/psikotest/psikotest_system/cfit";

// Component
import TableFilter from "../../../components/psikotest/psikotest_system/TableFilter";
import Table from "../../../components/psikotest/psikotest_system/Table";

function List() {
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  // required state
  const [list, setList] = useState<Array<TPSCfit>>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "cfit/List" });
  }, []);

  const getList = useCallback(async () => {
    try {
      setMessage("Fetch Cfit");
      const cc = new Cfit();
      const res = await cc.list();
      setList(res.data.list);
      setMessage("");
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [setNotif, setMessage]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <CfitList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function CfitList(props: { list: Array<TPSCfit>; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (cfit: TPSCfit) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const cc = new Cfit();
          await cc.delete(cfit);
          props.handleDeleteList(cfit.id);
          setNotif({ type: "success", message: "cfit deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Test Name (CFIT)",
        accessor: "test_name",
        show: isMobile,
        Cell: (props: { row: { original: TPSCfit } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.test_name || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "test_name",
        classHeader: "text-left px-2",
        Header: <div title="Test Name (CFIT)">Test Name (CFIT)</div>,
        classDivHeader: "flex items-center",
        accessor: "test_name",
        show: !isMobile,
        Cell: (props: { row: { original: TPSCfit } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="flex items-center text-blue-500 hover:text-blue-800">
              {props.row.original.test_name || "N/A"}
            </Link>
          );
        },
      },
      {
        id: "mdi",
        classHeader: "text-left px-2",
        Header: <div title="(MDI) Main Question | Description | Instructions">(MDI) Main Question | Description | Instructions</div>,
        classDivHeader: "flex items-center",
        accessor: "mdi",
        show: !isMobile,
        Cell: (props: { row: { original: TPSCfit } }) => {
          return props.row.original.mdi || "N/A"
        },
      },
      {
        id: "time_limit",
        classHeader: "text-left px-2",
        Header: <div title="Time Limit (Minutes)">Time Limit (Minutes)</div>,
        classDivHeader: "flex items-center",
        accessor: "time_limit",
        show: !isMobile,
        Cell: (props: { row: { original: TPSCfit } }) => {
          return props.row.original.time_limit
        },
      },
      {
        id: "minimum_selected_answer",
        classHeader: "text-left px-2",
        Header: <div title="Minimum of Selected Answer">MoSA</div>,
        classDivHeader: "flex items-center",
        accessor: "minimum_selected_answer",
        show: !isMobile,
        Cell: (props: { row: { original: TPSCfit } }) => {
          return props.row.original.minimum_selected_answer
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5 w-auto" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TPSCfit } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}

export default List;
